import React, {useEffect, useState, useRef} from 'react';
import {RouteComponentProps, Link} from 'react-router-dom';
import {Box, Typography, Breadcrumbs} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {connect} from 'react-redux';
import {
  IFdApplicationType,
  ISupplierUserProfile,
  IUser,
} from 'constants/interfaces';
import {
  apiGetDepotQuotes,
  apiGetDepotStatistics,
  apiGetUser,
  apiGetUserFdApplications,
} from 'constants/Api';
import WalletList from './Wallets/WalletList';

import {GoogleMap, LoadScript, Polygon} from '@react-google-maps/api';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faSeedling,
  faChevronRight,
  faCoins,
  faFileInvoice,
  faTruck,
  faHandshake,
  faComments,
  faEnvelope,
  faPhoneAlt,
  faTree,
  faMapMarkerAlt,
  faUser,
  faMoneyCheck,
  faLayerGroup,
  faCalculator,
  faExclamationCircle,
} from '@fortawesome/pro-regular-svg-icons';

import './CustomerDashboard.scss';

import {_NumberWithSpaces, GOOGLE_API_KEY} from 'constants/index';
import {apiGetContracts} from 'screens/Dashboard/Contracts/utils/graph';
import {NavigationService} from 'services/index';
const moment = require('moment');

interface MatchParams {
  customerUserId: string;
}

interface CustomerDashboardProps extends RouteComponentProps<MatchParams> {
  user: ISupplierUserProfile;
}

// Helper to parse latLong
function parseLatLong(latLong?: string) {
  if (!latLong) return {lat: -29.0, lng: 25.0};
  const parts = latLong.split(',');
  if (parts.length >= 2) {
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    if (!isNaN(lat) && !isNaN(lng)) {
      return {lat, lng};
    }
  }
  return {lat: -29.0, lng: 25.0};
}

function CustomerDashboard(props: CustomerDashboardProps) {
  const {customerUserId} = props.match.params;
  const mapRef = useRef<google.maps.Map | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [customerData, setCustomerData] = useState<IUser | null>(null);
  const [mapCoords, setMapCoords] = useState<{lat: number; lng: number}>({
    lat: -29.0,
    lng: 25.0,
  });
  const [polygons, setPolygons] = useState<any[]>([]);
  const [numQuotes, setNumQuotes] = useState(0);
  const [numOrders, setNumOrders] = useState(0);
  const [totalOrdersPastYear, setTotalOrdersPastYear] = useState(0);
  const [countContracts, setCountContracts] = useState(0);
  const [application, setApplication] = useState<IFdApplicationType>();

  // We'll track high+mid NDVI, Chlorophyll, moisture
  const [riskCounts, setRiskCounts] = useState({
    ndvi: {high: 0, mid: 0},
    chlorophyll: {high: 0, mid: 0},
    moisture: {high: 0, mid: 0},
  });

  useEffect(() => {
    setIsLoading(true);
    apiGetUser(customerUserId)
      .then(async (user) => {
        setCustomerData(user);
        if (user?.farmer?.farm?.latLong) {
          const coords = parseLatLong(user.farmer.farm.latLong);
          setMapCoords(coords);
          // fetch fields with .risks
          setPolygons(user.farmer.farm.fields ?? []);
        }

        // Get all statistics for this farmer
        const allTimeStats = await apiGetDepotStatistics(
          undefined,
          undefined,
          undefined,
          user.farmer.id,
        );
        setNumOrders(allTimeStats.invoicesCount);

        // Fetch quotes directly to match what's shown on quotes page
        // Use a high limit to get all quotes for this farmer (matching Quotes page behavior)
        // Convert farmer ID to string to match GraphQL ID type
        const farmerId = String(user.farmer.id);
        const quotes = await apiGetDepotQuotes(500, farmerId);
        // Filter quotes by farmer ID on client side to ensure accuracy
        // (in case API doesn't filter correctly or returns quotes for other farmers)
        const filteredQuotes = quotes.filter(
          (quote) => String(quote.farmer?.id) === farmerId,
        );
        setNumQuotes(filteredQuotes.length);

        // Get last year statistics with exact date range matching navigation
        const lastYearStart = moment()
          .subtract(1, 'year')
          .startOf('day')
          .toDate();
        const lastYearEnd = moment().endOf('day').toDate();
        const lastYearStats = await apiGetDepotStatistics(
          lastYearStart,
          lastYearEnd,
          undefined,
          user.farmer.id,
        );
        // Inputs last year shows paid invoices total (matches paid=1 filter on orders page)
        setTotalOrdersPastYear(lastYearStats.paidInvoicesTotal || 0);
        await apiGetContracts(user?.farmer?.id as any).then((contracts) => {
          setCountContracts(contracts?.length);
        });
      })
      .then(async () => {
        // todo new endpoint for user applications by externalUserId
        await apiGetUserFdApplications(customerUserId).then((applications) => {
          setApplication(applications?.[0]);
        });
      })
      .catch((err) => {
        console.error('Error fetching user', err);
      })
      .finally(() => setIsLoading(false));
  }, [customerUserId]);

  // Recompute whenever polygons changes
  useEffect(() => {
    // Reset counts
    const newCounts = {
      ndvi: {high: 0, mid: 0},
      chlorophyll: {high: 0, mid: 0},
      moisture: {high: 0, mid: 0},
    };

    polygons.forEach((field) => {
      if (!field.risks || field.risks.length === 0) return;
      const latestRisk = field.risks[field.risks.length - 1];

      // NDVI
      const ndviLevel = latestRisk?.ndviRiskLevel?.toLowerCase() || '';
      if (ndviLevel.includes('high')) newCounts.ndvi.high++;
      else if (ndviLevel.includes('moderate') || ndviLevel.includes('medium')) {
        newCounts.ndvi.mid++;
      }

      // Chlorophyll
      const chlLevel = latestRisk?.clRiskLevel?.toLowerCase() || '';
      if (chlLevel.includes('high')) newCounts.chlorophyll.high++;
      else if (chlLevel.includes('moderate') || chlLevel.includes('medium')) {
        newCounts.chlorophyll.mid++;
      }

      // Moisture
      const moistureLevel = latestRisk?.moistureRiskLevel?.toLowerCase() || '';
      if (moistureLevel.includes('high')) newCounts.moisture.high++;
      else if (
        moistureLevel.includes('moderate') ||
        moistureLevel.includes('medium')
      ) {
        newCounts.moisture.mid++;
      }
    });

    setRiskCounts(newCounts);
  }, [polygons]);

  const farmName = customerData?.farmer?.farm?.name || 'Farm Name';

  // Build an array for the top-right risk card
  // We'll show each label if there's at least 1 high or mid
  const riskIndicators = [
    {
      label: 'NDVI',
      high: riskCounts.ndvi.high,
      mid: riskCounts.ndvi.mid,
    },
    {
      label: 'Chlorophyll',
      high: riskCounts.chlorophyll.high,
      mid: riskCounts.chlorophyll.mid,
    },
    {
      label: 'Moisture',
      high: riskCounts.moisture.high,
      mid: riskCounts.moisture.mid,
    },
  ].filter((r) => r.high > 0 || r.mid > 0);

  // Removed handleTradedWidgetClick - "Traded last year" is no longer clickable

  const handleInputsLastYearClick = () => {
    if (!customerData?.id) return;
    // Calculate date range for last year
    const startDate = moment().subtract(1, 'year').startOf('day').toDate();
    const endDate = moment().endOf('day').toDate();
    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];
    NavigationService.navigate(
      `/orders?userId=${customerData.id}&paid=1&startDate=${startDateStr}&endDate=${endDateStr}`,
    );
  };

  // Hardcoded traded amount for all farmers
  const tradedAmount = 289741.06;

  return (
    <Box className="customer-dashboard-container">
      {/* Breadcrumb */}
      <Box mb={2} className="breadcrumb-row">
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to="/customers"
            style={{textDecoration: 'none', color: '#267373'}}
          >
            Farmers
          </Link>
          <Typography color="textPrimary" style={{fontWeight: 700}}>
            {farmName}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Main Grid */}
      <Box className="dashboard-grid">
        {/* PROFILE SECTION */}
        <Box className="profile-section card-section">
          {isLoading ? (
            <>
              <Skeleton variant="text" width={150} height={24} />
              <Skeleton
                variant="rect"
                width="100%"
                height={120}
                style={{marginTop: 16}}
              />
            </>
          ) : customerData ? (
            <Box className="profile-container">
              {customerData.image ? (
                <img
                  src={customerData.image}
                  alt="Profile"
                  className="profile-picture"
                />
              ) : (
                <Box className="profile-picture default">
                  <FontAwesomeIcon icon={faUser} />
                </Box>
              )}
              <Typography className="profile-name">
                {customerData.name} {customerData.surname}
              </Typography>
              <Box className="profile-details">
                <Box className="detail-row">
                  <FontAwesomeIcon icon={faEnvelope} className="detail-icon" />
                  {customerData.email}
                </Box>
                <Box className="detail-row">
                  <FontAwesomeIcon icon={faPhoneAlt} className="detail-icon" />
                  {customerData.phone}
                </Box>
                {customerData.farmer && (
                  <>
                    <Box className="detail-row">
                      <FontAwesomeIcon icon={faTree} className="detail-icon" />
                      {customerData.farmer.farm?.sizeHectares} hectares
                    </Box>
                    <Box className="detail-row">
                      <FontAwesomeIcon
                        icon={faMapMarkerAlt}
                        className="detail-icon"
                      />
                      {customerData.farmer.farm?.province}
                    </Box>
                  </>
                )}
              </Box>
              <Box className="chat-button">
                <FontAwesomeIcon icon={faComments} className="chat-icon" />
                <span>Chat with {customerData.name}</span>
              </Box>
            </Box>
          ) : (
            <Typography>No customer data found.</Typography>
          )}
        </Box>

        {/* WALLETS SECTION */}
        <Box className="wallet-section card-section">
          <WalletList customerUserId={customerUserId} />
        </Box>

        {/* MAP SECTION - no padding, map is 100% flush */}
        <Box className="map-section card-section no-padding">
          {isLoading ? (
            <Skeleton variant="rect" width="100%" height={180} />
          ) : (
            <Box className="mini-map-container">
              <LoadScript
                googleMapsApiKey={GOOGLE_API_KEY}
                libraries={['drawing', 'geometry', 'places']}
              >
                <GoogleMap
                  mapContainerStyle={{width: '100%', height: '100%'}}
                  zoom={16}
                  center={{lat: mapCoords?.lat, lng: mapCoords?.lng}}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                  mapTypeId="satellite"
                  options={{
                    mapTypeId: 'satellite',
                    disableDefaultUI: true,
                  }}
                >
                  {polygons?.map((polygon: any, index: number) => (
                    <Polygon
                      key={polygon.id || index}
                      paths={polygon.coordinates}
                      options={{
                        fillColor: 'lightblue',
                        fillOpacity: 0.4,
                        strokeColor: 'blue',
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        clickable: true,
                        editable: true,
                      }}
                    />
                  ))}
                </GoogleMap>
              </LoadScript>

              {/* Risk-Indicators Card (top-right) */}
              <Box className="map-risk-card">
                {riskIndicators.map((risk) => {
                  // Example label like: "NDVI (1 high, 2 mid fields)"
                  // or if mid=0, just show high. If high=0, just show mid. etc.
                  // We'll build a sub-label:
                  let display = '';
                  if (risk.high > 0 && risk.mid > 0) {
                    display = `${risk.high} high, ${risk.mid} mid`;
                  } else if (risk.high > 0) {
                    display = `${risk.high} high`;
                  } else if (risk.mid > 0) {
                    display = `${risk.mid} mid`;
                  }
                  return (
                    <Box key={risk.label} className="risk-item high">
                      <FontAwesomeIcon
                        icon={faExclamationCircle}
                        className="risk-icon"
                      />
                      {risk.label} ({display} fields)
                    </Box>
                  );
                })}
              </Box>

              <Box className="map-overlay-button">
                <button
                  onClick={() => {
                    NavigationService.navigate(
                      `/customers/dashboard/${customerUserId}/mapping`,
                    );
                  }}
                >
                  Open Map
                </button>
              </Box>
            </Box>
          )}
        </Box>

        {/* INPUTS CARD */}
        <Box
          className="inputs-card card-section"
          role="button"
          tabIndex={0}
          onClick={handleInputsLastYearClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleInputsLastYearClick();
            }
          }}
        >
          {isLoading ? (
            <Skeleton variant="rect" width="100%" height={80} />
          ) : (
            <Box className="summary-card">
              <Box className="icon-container">
                <FontAwesomeIcon icon={faSeedling} />
              </Box>
              <Box className="summary-bottom">
                <Typography className="summary-label">
                  Inputs last year
                </Typography>
                <Box className="value-row">
                  <Typography className="summary-value">
                    R {_NumberWithSpaces(totalOrdersPastYear)}
                  </Typography>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="value-chevron"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* TRADING CARD */}
        <Box className="trading-card card-section">
          {isLoading ? (
            <Skeleton variant="rect" width="100%" height={80} />
          ) : (
            <Box className="summary-card">
              <Box className="icon-container">
                <FontAwesomeIcon icon={faCoins} />
              </Box>
              <Box className="summary-bottom">
                <Typography className="summary-label">
                  Traded last year
                </Typography>
                <Box className="value-row">
                  <Typography className="summary-value">
                    R {_NumberWithSpaces(tradedAmount)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* QUOTES CARD */}
        <Box
          className="quotes-card card-section"
          onClick={() => {
            NavigationService.navigate(
              `/quotes?userId=${customerData?.id || ''}`,
            );
          }}
        >
          {isLoading ? (
            <Skeleton variant="rect" width="100%" height={80} />
          ) : (
            <Box className="summary-card">
              <Box className="icon-container">
                <FontAwesomeIcon icon={faFileInvoice} />
              </Box>
              <Box className="summary-bottom">
                <Typography className="summary-label">Quotes</Typography>
                <Box className="value-row">
                  <Typography className="summary-value">{numQuotes}</Typography>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="value-chevron"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* ORDERS CARD */}
        <Box
          className="orders-card card-section"
          onClick={() => {
            NavigationService.navigate(
              `/orders?userId=${customerData?.id || ''}`,
            );
          }}
        >
          {isLoading ? (
            <Skeleton variant="rect" width="100%" height={80} />
          ) : (
            <Box className="summary-card">
              <Box className="icon-container">
                <FontAwesomeIcon icon={faTruck} />
              </Box>
              <Box className="summary-bottom">
                <Typography className="summary-label">Orders</Typography>
                <Box className="value-row">
                  <Typography className="summary-value">{numOrders}</Typography>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="value-chevron"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* CONTRACTS CARD */}
        <Box
          className="contracts-card card-section pointer"
          onClick={() => {
            props.history.push(`/contracts/${customerUserId}`);
          }}
        >
          {isLoading ? (
            <Skeleton variant="rect" width="100%" height={80} />
          ) : (
            <Box className="summary-card">
              <Box className="icon-container">
                <FontAwesomeIcon icon={faHandshake} />
              </Box>
              <Box className="summary-bottom flex-1" style={{flex: 1}}>
                <Typography className="summary-label">Contracts</Typography>
                <Box className="value-row">
                  <Typography className="summary-value">
                    {countContracts}
                  </Typography>
                </Box>
              </Box>
              <FontAwesomeIcon
                icon={faChevronRight}
                className="value-chevron"
              />
            </Box>
          )}
        </Box>

        {/* MENU SECTION */}
        <Box className="menu-section card-section">
          <Box className="menu-list" mt={2}>
            {/* Bookkeeping AI */}
            <Box
              className="menu-item"
              onClick={() => {
                props.history.push(
                  `/customers/dashboard/${customerUserId}/bookkeeping-ai`,
                );
              }}
            >
              <Box className="icon-container ai-gradient">
                <FontAwesomeIcon icon={faCalculator} />
              </Box>
              <Typography className="menu-label">Bookkeeping AI</Typography>
              <FontAwesomeIcon icon={faChevronRight} className="menu-chevron" />
            </Box>

            {/* Funding applications */}
            {application && (
              <Box
                className="menu-item"
                onClick={() => {
                  props.history.push({
                    pathname: `/applications/${application.id}`,
                    state: {origin: 'farmers'},
                  });
                }}
              >
                <Box className="icon-container">
                  <FontAwesomeIcon icon={faMoneyCheck} />
                </Box>
                <Typography className="menu-label">
                  Funding applications
                </Typography>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="menu-chevron"
                />
              </Box>
            )}
            {/* Programs */}
            <Box
              className="menu-item"
              onClick={() => {
                NavigationService.navigate(
                  `/programs?userId=${customerData?.id || ''}`,
                );
              }}
            >
              <Box className="icon-container">
                <FontAwesomeIcon icon={faLayerGroup} />
              </Box>
              <Typography className="menu-label">Programs</Typography>
              <FontAwesomeIcon icon={faChevronRight} className="menu-chevron" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user as ISupplierUserProfile,
});

export default connect(mapStateToProps)(CustomerDashboard);
